/* eslint-disable react-refresh/only-export-components */
import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { patientApi } from '../lib/api';
import { usePatientAuth } from './PatientAuthContext';
import { extractData } from '../hooks/useQueries';

export interface LinkedDependent {
  dependentLinkId: string;
  dependentPatientId: string;
  dependentClinicId: string;
  clinicName?: string;
  name: string;
  relationshipType: string;
  status: 'PENDING_CONSENT' | 'ACTIVE' | 'CONSENT_EXPIRED' | 'REVOKED' | 'AGED_OUT_PENDING_RECONSENT';
  isMinor: boolean;
  activeConsentStatus?: string;
  activeConsentMethod?: string;
  validUntil?: string;
  createdAt: string;
}

const ACTIVE_DEPENDENT_KEY = 'active_dependent_patient_id';

interface PatientDependentContextType {
  dependents: LinkedDependent[];
  /** null means the account holder is viewing their own records. */
  activeDependent: LinkedDependent | null;
  loading: boolean;
  refresh: () => Promise<void>;
  /**
   * Switches to viewing a dependent's records. Validated against the already-fetched dependent
   * list (must be status ACTIVE) before committing — catches a stale/revoked entry here rather
   * than as a raw 403 mid-navigation, matching the backend filter's fail-closed posture.
   * Returns false (no-op) if the dependent isn't found or isn't ACTIVE.
   */
  switchToDependent: (dependentPatientId: string) => boolean;
  /** Returns to viewing the account holder's own records. */
  switchToSelf: () => void;
}

const PatientDependentContext = createContext<PatientDependentContextType>({
  dependents: [],
  activeDependent: null,
  loading: true,
  refresh: async () => {},
  switchToDependent: () => false,
  switchToSelf: () => {},
});

export function PatientDependentProvider({ children }: { children: React.ReactNode }) {
  const { patient } = usePatientAuth();
  const [dependents, setDependents] = useState<LinkedDependent[]>([]);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (!patient) {
      setDependents([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const res = await patientApi.dependents.list();
      setDependents(extractData<LinkedDependent[]>(res ?? {}) ?? []);
    } catch {
      setDependents([]);
    } finally {
      setLoading(false);
    }
  }, [patient?.id]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const overrideId = sessionStorage.getItem(ACTIVE_DEPENDENT_KEY);
  const activeDependent =
    (overrideId && dependents.find((d) => d.dependentPatientId === overrideId && d.status === 'ACTIVE')) || null;

  // The stored id no longer resolves to an ACTIVE dependent (revoked/aged-out/unlinked since it
  // was set) — clear it so the app falls back to "self" instead of silently retrying a switch
  // that the backend will 403 on the next request.
  useEffect(() => {
    if (!loading && overrideId && !activeDependent) {
      sessionStorage.removeItem(ACTIVE_DEPENDENT_KEY);
    }
  }, [loading, overrideId, activeDependent]);

  const switchToDependent = useCallback((dependentPatientId: string): boolean => {
    const target = dependents.find((d) => d.dependentPatientId === dependentPatientId && d.status === 'ACTIVE');
    if (!target) return false;
    sessionStorage.setItem(ACTIVE_DEPENDENT_KEY, dependentPatientId);
    // Reload so every screen's already-in-flight queries re-fetch under the new dependent scope.
    window.location.href = '/patient/dashboard';
    return true;
  }, [dependents]);

  const switchToSelf = useCallback(() => {
    sessionStorage.removeItem(ACTIVE_DEPENDENT_KEY);
    window.location.href = '/patient/dashboard';
  }, []);

  return (
    <PatientDependentContext.Provider value={{
      dependents, activeDependent, loading, refresh, switchToDependent, switchToSelf,
    }}>
      {children}
    </PatientDependentContext.Provider>
  );
}

export function usePatientDependents() {
  return useContext(PatientDependentContext);
}
